import { GLOBAL_CONSTANTS } from "../../constants";

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;
const MSK_DELIVERY_TRACE_LOG = "MSK_DELIVERY_TRACE";

const readErrorMessage = async (response) => {
  try {
    const payload = await response.json();
    return payload?.error || payload?.message || JSON.stringify(payload);
  } catch (jsonError) {
    try {
      return await response.text();
    } catch (textError) {
      return "";
    }
  }
};

// 🔹 Главная функция: разделяет материалы по категориям и вызывает обработку только для непустых категорий
export const calculateDeliveryCost = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, skipNonmetallic = false, signal) => {
  const groupedMaterials = groupMaterialsByCategory(materials, selectedRegion);
  const validCategories = Object.entries(groupedMaterials)
    .filter(([category, mats]) => {
      // Пропускаем нерудку, если используется специализированный режим
      if (skipNonmetallic && category === "ner") {
        console.log("🔄 Пропускаем обработку нерудки в обычной логике - используется специализированный режим");
        return false;
      }
      return mats.length > 0;
    });

  const results = await Promise.all(validCategories.map(async ([category, mats]) => {
    try {
      const result = await processCategory(category, mats, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal);
      return result ? { category, result } : null;
    } catch (error) {
      if (error?.name === "AbortError") {
        throw error;
      }
      console.error(`Ошибка "${category}":`, error);
      return null;
    }
  }));

  return Object.fromEntries(
    results.filter(Boolean)
      .map(({ category, result }) => [category, result])
  );
};

// 🔹 Главная функция обработки категории: вызывает нужную обработку
const processCategory = async (category, materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal) => {
  switch (category) {
    case "os":
      return await processOsCategory(materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, signal);
    case "jbi":
      return await processJbiCategory(materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal);
    case "gaz":
      return await processGazCategory(
        materials,
        deliveryMethod,
        productionInfo,
        selfPickUp,
        addressAndCoordinates,
        selectedRegion,
        paymentMethod,
        signal
      );
    case "ner":
      return await processNerCategory(materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal);
    case "mix": // Добавлена новая категория "Бетон"
      return await processMixCategory(materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, signal);
    case "pilmat": // Новая категория
      return await processPilmatCategory(materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, signal);
    case "grandline": // Металлочерепица, профлист и штакетник
      return await processGrandlineCategory(materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, signal);
    case "metall": // Металл (арматура, труба, уголок и т.д.)
      return await processMetallCategory(materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal);
    default:
      return processOtherCategory(materials, deliveryMethod, productionInfo, selfPickUp);
  }
};

// 🔹 Функции обработки для каждой категории
const processOsCategory = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, signal) => {
  console.log("📦 Обработка категории 'Общестрой':", materials);

  try {
    const updatedAddressAndCoordinates = {
      ...addressAndCoordinates,
      region: selectedRegion,
    };

    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_os`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify((() => {
        const payload = {
          materials,
          deliveryMethod: deliveryMethod?.os || "Оптимальный маршрут",
          mskDeliveryMode: deliveryMethod?.mskDeliveryMode || "factory_to_client",
          addressAndCoordinates: updatedAddressAndCoordinates,
          productionInfo
        };

        console.log(MSK_DELIVERY_TRACE_LOG, {
          layer: "client",
          source: "calculateDelivery",
          category: "os",
          event: "request",
          payload,
        });

        return payload;
      })()),
    });

    if (!response.ok) {
      const data = await response.json();

      // Обработка разных статусов
      switch (response.status) {
        case 400:
          return {
            category: "Общестрой",
            details: data.error || "Не хватает данных для расчета доставки",
            error: true
          };

        case 422:
          return {
            category: "Общестрой",
            details: data.error || "Проверьте корректность введенных данных",
            error: true
          };

        case 502:
          return {
            category: "Общестрой",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };

        case 500:
        default:
          return {
            category: "Общестрой",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
      }
    }

    const data = await response.json();
    return { category: "Общестрой", details: data };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }
    console.error("❌ Ошибка при запросе к /api/delivery_os:", error);
    return {
      category: "Общестрой",
      details: "Ошибка при отправке запроса. Проверьте соединение.",
      error: true
    };
  }
};

const processJbiCategory = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal) => {
  try {
    const totalMass = materials.reduce((acc, material) => {
      const quantity = Number(material.quantity) || 0;
      const massPerOne = Number((material.massPerOne || "").toString().replace(",", ".")) || 0;
      const itemWeight = quantity * massPerOne;
      console.log(`📦 Товар: ${material.name}, Количество: ${quantity}, Масса за единицу: ${massPerOne}, Общая масса товара: ${itemWeight}`);
      return acc + itemWeight;
    }, 0);

    console.log(`📦 Итоговая масса для категории "ЖБИ": ${totalMass} т`);

    // Добавляем selectedRegion в addressAndCoordinates для передачи на сервер
    const updatedAddressAndCoordinates = {
      ...addressAndCoordinates,
      region: selectedRegion,
    };

    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_jbi`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify((() => {
        const payload = {
          materials,
          deliveryMethod: deliveryMethod?.jbi || "Оптимальный маршрут",
          mskDeliveryMode: deliveryMethod?.mskDeliveryMode || "factory_to_client",
          addressAndCoordinates: updatedAddressAndCoordinates,
          productionInfo,
          paymentMethod: paymentMethod || 'cash'
        };

        console.log(MSK_DELIVERY_TRACE_LOG, {
          layer: "client",
          source: "calculateDelivery",
          category: "jbi",
          event: "request",
          payload,
        });

        return payload;
      })()),
    });

    if (!response.ok) {
      const data = await response.json();

      switch (response.status) {
        case 400:
          return {
            category: "ЖБИ",
            details: data.error || "Не хватает данных для расчета доставки",
            error: true
          };
        case 422:
          return {
            category: "ЖБИ",
            details: data.error || "Проверьте корректность введенных данных",
            error: true
          };
        case 502:
          return {
            category: "ЖБИ",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
        case 500:
        default:
          return {
            category: "ЖБИ",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
      }
    }

    const deliveryData = await response.json();
    console.log(`✅ Ответ от /api/delivery_jbi:`, deliveryData);

    return { category: "ЖБИ", details: [{ groupKey: "all", deliveryData }] };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }
    console.error("❌ Ошибка /api/delivery_jbi:", error);


    return { category: "ЖБИ", details: "Ошибка запроса" };
  }
};

const processGazCategory = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal) => {
  console.log("🧱 Обработка категории 'Газобетон':", materials);

  if (!addressAndCoordinates) {
    console.error("❌ Ошибка: Координаты клиента не переданы!");
    return { category: "Газобетон и кирпич", details: "Ошибка: координаты клиента отсутствуют." };
  }

  try {
    const updatedAddressAndCoordinates = {
      ...addressAndCoordinates,
      region: selectedRegion,
    };

    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_gaz`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        materials,
        deliveryMethod: deliveryMethod?.gaz || "Оптимальный маршрут",
        unloadMethod: deliveryMethod?.gazUnloadMethod || "без разгрузки",
        addressAndCoordinates: updatedAddressAndCoordinates,
        productionInfo,
        paymentMethod: paymentMethod || "cash"
      }),
    });

    if (!response.ok) {
      const data = await response.json();

      switch (response.status) {
        case 400:
          return {
            category: "Газобетон и кирпич",
            details: data.error || "Не хватает данных для расчета доставки",
            error: true
          };
        case 422:
          return {
            category: "Газобетон и кирпич",
            details: data.error || "Проверьте корректность введенных данных",
            error: true
          };
        case 502:
          return {
            category: "Газобетон и кирпич",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
        case 500:
        default:
          return {
            category: "Газобетон и кирпич",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
      }
    }

    const data = await response.json();
    return { category: "Газобетон и кирпич", details: data };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }
    console.error("❌ Ошибка при запросе к /api/delivery_gaz:", error);


    return {
      category: "Газобетон и кирпич",
      details: {
        error: error?.message || "Ошибка при отправке запроса",
      },
    };
  }
};

const processNerCategory = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal) => {
  console.log("⛏️ Обработка категории 'Нерудные материалы':", materials);

  const totalVolume = materials.reduce((acc, material) => {
    const quantity = Number(material.quantity) || 0;
    const volumePerOne = Number((material.volumePerOne || "").toString().replace(",", ".")) || 0;
    const itemVolume = quantity * volumePerOne;
    console.log(`📦 Товар: ${material.name}, Количество: ${quantity}, Объём за единицу: ${volumePerOne}, Общий объём товара: ${itemVolume}`);
    return acc + itemVolume;
  }, 0);

  console.log(`📦 Итоговый объём для категории "Нерудные материалы": ${totalVolume} м³`);
  console.log(`💳 Способ оплаты: ${paymentMethod}`);

  if (!addressAndCoordinates) {
    console.error("❌ Ошибка: Координаты клиента не переданы!");
    return { category: "Нерудные материалы", details: "Ошибка: координаты клиента отсутствуют." };
  }

  // Определяем категорию материала из первого товара (все товары в группе имеют одинаковую категорию)  
  const materialCategory = materials[0]?.category || "Песок"; // Значение по умолчанию

  try {
    // Добавляем selectedRegion в addressAndCoordinates для передачи на сервер
    const updatedAddressAndCoordinates = {
      ...addressAndCoordinates,
      region: selectedRegion,
    };

    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_ner`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        materials,
        deliveryMethod: deliveryMethod?.ner || "Оптимальный маршрут",
        addressAndCoordinates: updatedAddressAndCoordinates,
        productionInfo,
        paymentMethod,
        category: materialCategory
      }),
    });

    if (!response.ok) {
      const data = await response.json();

      switch (response.status) {
        case 400:
          return {
            category: "Нерудные материалы",
            details: data.error || "Не хватает данных для расчета доставки",
            error: true
          };
        case 422:
          return {
            category: "Нерудные материалы",
            details: data.error || "Проверьте корректность введенных данных",
            error: true
          };
        case 502:
          return {
            category: "Нерудные материалы",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
        case 500:
        default:
          return {
            category: "Нерудные материалы",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
      }
    }

    const data = await response.json();
    console.log(`✅ Ответ от /api/delivery_ner:`, data);
    return { category: "Нерудные материалы", details: data };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }


    console.error("❌ Ошибка при запросе к /api/delivery_ner:", error);
    return { category: "Нерудные материалы", details: "Ошибка при отправке запроса" };
  }
};

// Новая функция для обработки категории "Бетон"
const processMixCategory = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, signal) => {
  console.log("🚛 Обработка категории 'Бетон':", materials);

  const totalVolume = materials.reduce((acc, material) => {
    const quantity = Number(material.quantity) || 0;
    const volumePerOne = Number((material.volumePerOne || "").toString().replace(",", ".")) || 0;
    const itemVolume = quantity * volumePerOne;
    console.log(`📦 Товар: ${material.name}, Количество: ${quantity}, Объём за единицу: ${volumePerOne}, Общий объём товара: ${itemVolume}`);
    return acc + itemVolume;
  }, 0);

  console.log(`📦 Итоговый объём для категории "Бетон": ${totalVolume} м³`);

  if (!addressAndCoordinates) {
    console.error("❌ Ошибка: Координаты клиента не переданы!");
    return { category: "Бетон", details: "Ошибка: координаты клиента отсутствуют." };
  }

  try {
    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_mix`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        materials,
        deliveryMethod: deliveryMethod?.mix || "Оптимальный маршрут",
        addressAndCoordinates,
        productionInfo
      }),
    });

    if (!response.ok) {
      const data = await response.json();

      switch (response.status) {
        case 400:
          return {
            category: "Бетон",
            details: data.error || "Не хватает данных для расчета доставки",
            error: true
          };
        case 422:
          return {
            category: "Бетон",
            details: data.error || "Проверьте корректность введенных данных",
            error: true
          };
        case 502:
          return {
            category: "Бетон",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
        case 500:
        default:
          return {
            category: "Бетон",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
      }
    }

    const data = await response.json();
    console.log(`✅ Ответ от /api/delivery_mix:`, data);
    return { category: "Бетон", details: data };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }


    console.error("❌ Ошибка при запросе к /api/delivery_mix:", error);
    return { category: "Бетон", details: "Ошибка при отправке запроса" };
  }
};

const processPilmatCategory = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, signal) => {
  console.log("🌲 Обработка категории 'Пиломатериалы':", materials);

  if (!addressAndCoordinates) {
    console.error("❌ Ошибка: Координаты клиента не переданы!");
    return { category: "Пиломатериалы", details: "Ошибка: координаты клиента отсутствуют." };
  }

  try {
    // Добавляем selectedRegion в addressAndCoordinates для передачи на сервер
    const updatedAddressAndCoordinates = {
      ...addressAndCoordinates,
      region: selectedRegion,
    };

    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_pilmat`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify((() => {
        const payload = {
          materials,
          deliveryMethod: deliveryMethod?.pilmat || "Оптимальный маршрут",
          mskDeliveryMode: deliveryMethod?.mskDeliveryMode || "factory_to_client",
          addressAndCoordinates: updatedAddressAndCoordinates,
          productionInfo
        };

        console.log(MSK_DELIVERY_TRACE_LOG, {
          layer: "client",
          source: "calculateDelivery",
          category: "pilmat",
          event: "request",
          payload,
        });

        return payload;
      })()),
    });

    if (!response.ok) {
      const data = await response.json();

      switch (response.status) {
        case 400:
          return {
            category: "Пиломатериалы",
            details: data.error || "Не хватает данных для расчета доставки",
            error: true
          };
        case 422:
          return {
            category: "Пиломатериалы",
            details: data.error || "Проверьте корректность введенных данных",
            error: true
          };
        case 502:
          return {
            category: "Пиломатериалы",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
        case 500:
        default:
          return {
            category: "Пиломатериалы",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
      }
    }

    const data = await response.json();
    console.log(`✅ Ответ от /api/delivery_pilmat:`, data);
    return { category: "Пиломатериалы", details: data };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }


    console.error("❌ Ошибка при запросе к /api/delivery_pilmat:", error);
    return { category: "Пиломатериалы", details: "Ошибка при отправке запроса" };
  }
};

const processGrandlineCategory = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, signal) => {
  console.log("🏗️ Обработка категории 'Металлочерепица, профлист и штакетник':", materials);

  if (!addressAndCoordinates) {
    console.error("❌ Ошибка: Координаты клиента не переданы!");
    return { category: "Металлочерепица, профлист и штакетник", details: "Ошибка: координаты клиента отсутствуют." };
  }

  try {
    // Добавляем selectedRegion в addressAndCoordinates для передачи на сервер
    const updatedAddressAndCoordinates = {
      ...addressAndCoordinates,
      region: selectedRegion,
    };

    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_grandline`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        materials,
        deliveryMethod: deliveryMethod?.grandline || "ПРАЙС ГРАНДЛАЙН",
        addressAndCoordinates: updatedAddressAndCoordinates,
        productionInfo
      }),
    });

    if (!response.ok) {
      const data = await response.json();

      switch (response.status) {
        case 400:
          return {
            category: "Металлочерепица, профлист и штакетник",
            details: data.error || "Не хватает данных для расчета доставки",
            error: true
          };
        case 422:
          return {
            category: "Металлочерепица, профлист и штакетник",
            details: data.error || "Проверьте корректность введенных данных",
            error: true
          };
        case 502:
          return {
            category: "Металлочерепица, профлист и штакетник",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
        case 500:
        default:
          return {
            category: "Металлочерепица, профлист и штакетник",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
      }
    }

    const data = await response.json();
    console.log(`✅ Ответ от /api/delivery_grandline:`, data);
    return { category: "Металлочерепица, профлист и штакетник", details: data };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }


    console.error("❌ Ошибка при запросе к /api/delivery_grandline:", error);
    return { category: "Металлочерепица, профлист и штакетник", details: "Ошибка при отправке запроса" };
  }
};

const processMetallCategory = async (materials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, selectedRegion, paymentMethod, signal) => {
  console.log("🔩 Обработка категории 'Металл':", materials);

  if (!addressAndCoordinates) {
    console.error("❌ Ошибка: Координаты клиента не переданы!");
    return { category: "Металл", details: "Ошибка: координаты клиента отсутствуют." };
  }

  try {
    const updatedAddressAndCoordinates = {
      ...addressAndCoordinates,
      region: selectedRegion,
    };

    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_metall`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        materials,
        deliveryMethod: deliveryMethod?.metall || "Оптимальный маршрут",
        addressAndCoordinates: updatedAddressAndCoordinates,
        productionInfo,
        paymentMethod: paymentMethod || "cash"
      }),
    });

    if (!response.ok) {
      const data = await response.json();

      switch (response.status) {
        case 400:
          return {
            category: "Металл",
            details: data.error || "Не хватает данных для расчета доставки",
            error: true
          };
        case 422:
          return {
            category: "Металл",
            details: data.error || "Проверьте корректность введенных данных",
            error: true
          };
        case 502:
          return {
            category: "Металл",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
        case 500:
        default:
          return {
            category: "Металл",
            details: data.error || "Техническая ошибка. Сообщите в поддержку.",
            error: true
          };
      }
    }

    const data = await response.json();
    console.log(`✅ Ответ от /api/delivery_metall:`, data);
    return { category: "Металл", details: data };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }

    console.error("❌ Ошибка при запросе к /api/delivery_metall:", error);
    return { category: "Металл", details: "Ошибка при отправке запроса" };
  }
};

const processOtherCategory = (materials, deliveryMethod, productionInfo, selfPickUp) => {
  console.log("❓ Обработка неизвестной категории:", materials);
  return { category: "Прочее", details: `Обработано ${materials.length} материалов.` };
};

const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
  "бордюр",
  "бордюр дорожный",
  "брусчатка",
  "брусчатка и тротуарная плитка",
  "плитка тротуарная",
  "газонная решетка",
  "лоток водоотводный жби",
  "лоток водоотводный тротуарный",
]);

const normalizeRegionCode = (value) => {
  const rawRegion = String(value || "").trim().toUpperCase();
  if (rawRegion === "МОСКВА" || rawRegion === "МОСКОВСКАЯ ОБЛАСТЬ") {
    return "МСК";
  }
  if (rawRegion === "САНКТ-ПЕТЕРБУРГ" || rawRegion === "ЛЕНИНГРАДСКАЯ ОБЛАСТЬ") {
    return "СПБ";
  }
  return rawRegion;
};

const shouldUseGazDeliveryCategoryByRegion = (region) => (
  ["МСК", "КРД"].includes(normalizeRegionCode(region))
);

const isGazDeliveryCategory = (value, region) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized.includes("газобетон")) {
    return true;
  }

  if (!GAZ_DELIVERY_CATEGORY_NAMES.has(normalized)) {
    return false;
  }

  return shouldUseGazDeliveryCategoryByRegion(region);
};

const isRegionalGikPriceSource = (source, region) => {
  const normalizedRegion = normalizeRegionCode(region);
  const normalizedSource = String(source || "").trim().toUpperCase();
  return ["МСК", "КРД"].includes(normalizedRegion) &&
    normalizedSource.includes(`ПРАЙС ГИК ${normalizedRegion}`);
};

// 🔹 Функция группировки материалов по категории
const groupMaterialsByCategory = (materials, selectedRegion) => {
  const categorizedMaterials = Object.fromEntries(
    Object.keys(GLOBAL_CONSTANTS.CATEGORY_NAMES).map(key => [key, []])
  );
  categorizedMaterials["other"] = [];

  const categoryMap = new Map(
    Object.entries(GLOBAL_CONSTANTS.CATEGORY_NAMES)
      .map(([key, value]) => [value.trim().toLowerCase(), key])
  );

  materials.forEach(material => {
    const materialRegion = material.region || selectedRegion;
    const calculator = String(material.calculator || "").trim().toLowerCase();
    const category = String(material.category || "").trim().toLowerCase();
    const productGroup = String(material.product_group || "").trim().toLowerCase();
    const categoryKey = isGazDeliveryCategory(calculator, materialRegion) ||
      isGazDeliveryCategory(category, materialRegion) ||
      isGazDeliveryCategory(productGroup, materialRegion) ||
      isRegionalGikPriceSource(material.source, materialRegion)
      ? "gaz"
      : categoryMap.get(calculator) || "other";
    categorizedMaterials[categoryKey].push(material);
  });

  return categorizedMaterials;
};
